import React, { useEffect, useState } from 'react'
import {
    Box, Button, FormControl, FormControlLabel, InputLabel,
    MenuItem, Select, Switch, Tooltip, Typography
} from '@mui/material'
import { Close } from '@mui/icons-material'
import api from '../services/api'
import EmpresaSelect from './EmpresaSelect'

// Admin e cliente rodam na mesma origem (/admin e /hermes), então dividem o
// localStorage. Sem namespace, o filtro que o operador deixou salvo no admin
// reaparecia na tela do cliente logado no mesmo navegador.
const storeName = (admin) => (admin ? 'hermes' : 'hermes.client')

const readStore = (admin) => {
    try {
        return JSON.parse(localStorage.getItem(storeName(admin)) || '{}')
    } catch (e) {
        return {}
    }
}

const writeStore = (admin, patch) => {
    try {
        const stored = readStore(admin)
        localStorage.setItem(storeName(admin), JSON.stringify({ ...stored, ...patch }))
    } catch (e) {
        // localStorage cheio ou bloqueado não pode impedir o filtro de aplicar.
    }
}

export const emptyHostFilters = {
    empresa: null,
    typeFilter: '',
    backupFilter: '',
    hostStatus: '1',
    queueOnly: false,
}

export const initialHostFilters = (admin = false) => {
    const stored = readStore(admin)
    return {
        ...emptyHostFilters,
        empresa: admin ? (stored.empresa ?? null) : null,
        typeFilter: stored.type_filter ?? '',
        backupFilter: stored.backup_filter ?? '',
        hostStatus: stored.host_status ?? '1',
    }
}

// Converte o estado da barra no corpo que a listagem manda pro servidor.
// `queueOnly` sobrepõe o status do backup porque o backend aceita um critério
// só — por isso o select fica desabilitado enquanto o switch está ligado.
export const toDatatableFilters = (value) => ({
    type_id: value.typeFilter || undefined,
    backup_filter: value.queueOnly ? 'queued' : (value.backupFilter || undefined),
    host_status: value.hostStatus !== '' ? value.hostStatus : undefined,
})

/**
 * Barra de filtros da listagem de Hosts, usada pelo admin e pelo cliente.
 * A diferença é o `admin`: liga o seletor de Empresa (no cliente a empresa
 * vem do token) e escolhe de qual rota vêm os tipos.
 *
 * `children` são os botões próprios de cada tela (adicionar host, Zabbix,
 * backup em massa, sync), que ficam na mesma linha dos filtros.
 */
export default function HostFilterBar({ admin = false, value, onChange, onTypesLoaded, children }) {
    const [types, setTypes] = useState([])

    useEffect(() => {
        // No cliente o filtro so oferece o que a empresa tem host — o catalogo
        // inteiro sao dezenas de modelos que dariam resultado vazio.
        api.get(admin ? '/admin/hermes/host/type/all' : '/hermes/host/type/all?em_uso=1')
            .then((res) => {
                const loaded = res.data || []
                setTypes(loaded)
                // A tela ainda precisa da lista pra traduzir o id do tipo no
                // nome que o backup em massa espera — evita a mesma requisição
                // sair duas vezes.
                if (onTypesLoaded) onTypesLoaded(loaded)
            })
            .catch(() => setTypes([]))
    }, [admin])

    // O botao de limpar so faz sentido quando ha o que limpar — desabilitado
    // no estado padrao, ele mesmo diz que nao ha filtro aplicado.
    const sujo = !!(value.empresa || value.typeFilter || value.backupFilter
        || value.queueOnly || value.hostStatus !== emptyHostFilters.hostStatus)

    const set = (patch, stored = {}) => {
        writeStore(admin, stored)
        onChange({ ...value, ...patch })
    }

    return (
        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" sx={{ mb: 2, px: 1 }}>
            {admin && (
                <EmpresaSelect
                    sx={{ minWidth: 300 }}
                    value={value.empresa}
                    handler={(e) => set({ empresa: e }, { empresa: e })}
                />
            )}

            <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel>Tipo/Modelo</InputLabel>
                <Select
                    value={value.typeFilter}
                    label="Tipo/Modelo"
                    onChange={(e) => set({ typeFilter: e.target.value }, { type_filter: e.target.value })}
                >
                    <MenuItem value="">Todos</MenuItem>
                    {types.map((t) => (
                        <MenuItem key={t.id} value={t.id}>
                            {t.vendor} - {t.type} ({t.protocol})
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>

            <Tooltip title={value.queueOnly
                ? 'Desligue "Na Fila" para filtrar por status do backup'
                : ''}>
                <span>
                    <FormControl size="small" sx={{ minWidth: 160 }} disabled={value.queueOnly}>
                        <InputLabel>Status Backup</InputLabel>
                        <Select
                            value={value.backupFilter}
                            label="Status Backup"
                            onChange={(e) => set({ backupFilter: e.target.value }, { backup_filter: e.target.value })}
                        >
                            <MenuItem value="">Todos</MenuItem>
                            <MenuItem value="error">Com Erro</MenuItem>
                            <MenuItem value="ok">OK</MenuItem>
                            <MenuItem value="new">Sem Backup</MenuItem>
                        </Select>
                    </FormControl>
                </span>
            </Tooltip>

            <FormControl size="small" sx={{ minWidth: 130 }}>
                <InputLabel>Host Status</InputLabel>
                <Select
                    value={value.hostStatus}
                    label="Host Status"
                    onChange={(e) => set({ hostStatus: e.target.value }, { host_status: e.target.value })}
                >
                    <MenuItem value="1">Ativos</MenuItem>
                    <MenuItem value="0">Inativos</MenuItem>
                    <MenuItem value="">Todos</MenuItem>
                </Select>
            </FormControl>

            <FormControlLabel
                control={
                    <Switch
                        checked={value.queueOnly}
                        onChange={(e) => set({ queueOnly: e.target.checked })}
                        color="info"
                    />
                }
                label={<Typography variant="body2" sx={{ fontSize: '0.85rem' }}>Na Fila</Typography>}
                sx={{ ml: 0 }}
            />

            <Button
                size="small"
                startIcon={<Close />}
                disabled={!sujo}
                onClick={() => {
                    try { localStorage.removeItem(storeName(admin)) } catch (e) { }
                    onChange({ ...emptyHostFilters })
                }}
                sx={{ textTransform: 'none' }}
            >
                Limpar filtros
            </Button>

            {children}
        </Box>
    )
}
